import React, { useState, useRef, useEffect, DragEvent, ChangeEvent } from 'react';
import { FaUpload, FaTrash } from 'react-icons/fa';
import { ImSpinner2 } from 'react-icons/im';
import Modal from '@oncolens/oncolens-ui/lib/components/Common/Modal';
import DefaultButton, {
  DefaultButtonColor,
} from '@oncolens/oncolens-ui/lib/components/FormControls/DefaultButton';
import { clients } from '@oncolens/oncolens-ui/lib/api/client';
import OncolensToast from '@/components/OncolensToast';
import useToggle from '@/hooks/useToggle';
import { useUploadOrganizationLogo } from '../api/uploadOrganizationLogo';
import { useDeleteOrganizationLogo } from '../api/deleteOrganizationLogo';

interface OrganizationLogoUploadProps {
  organizationId: string;
  currentLogoUrl?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
const ACCEPTED_EXTENSIONS = ['.png', '.jpg', '.jpeg'];

// Helper function to get the full logo URL with cache-busting timestamp
// The timestamp query parameter forces the browser to fetch a fresh image when the logo changes,
// since the backend returns the same URL path even after uploading a new logo
const getLogoUrl = (organizationId: string, timestamp: number) => {
  const baseURL = clients.admin.defaults.baseURL || '';
  return `${baseURL}/organizations/${organizationId}/logo?t=${timestamp}`;
};

export function OrganizationLogoUpload({
  organizationId,
  currentLogoUrl,
}: OrganizationLogoUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [showDeleteModal, toggleDeleteModal] = useToggle(false);
  const [logoTimestamp, setLogoTimestamp] = useState(Date.now());
  const [isDeleted, setIsDeleted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update timestamp when currentLogoUrl changes (when parent data refetches)
  useEffect(() => {
    if (currentLogoUrl) {
      setLogoTimestamp(Date.now());
      setIsDeleted(false); // Reset deleted flag when we have a new logo
    }
  }, [currentLogoUrl]);

  const uploadMutation = useUploadOrganizationLogo(organizationId, {
    onSuccess: () => {
      setLogoTimestamp(Date.now()); // Update timestamp immediately to bust cache
      OncolensToast.success('Logo uploaded successfully');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.title || 'Failed to upload logo';
      OncolensToast.error(errorMessage);
    },
  });

  const deleteMutation = useDeleteOrganizationLogo(organizationId, {
    onSuccess: () => {
      OncolensToast.success('Logo removed successfully');
      setIsDeleted(true); // Mark as deleted immediately
      toggleDeleteModal();
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.title || 'Failed to remove logo';
      OncolensToast.error(errorMessage);
    },
  });

  const hasLogo = !isDeleted && currentLogoUrl;
  const isUploading = uploadMutation.isLoading;

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      return `Invalid file type. Please upload a ${ACCEPTED_EXTENSIONS.join(', ')} file.`;
    }

    if (file.size > MAX_FILE_SIZE) {
      return 'File size exceeds 5MB limit.';
    }

    return null;
  };

  const handleFileUpload = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      OncolensToast.error(validationError);
      return;
    }

    // Reset deleted flag and upload file
    setIsDeleted(false);
    uploadMutation.mutate(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const { files } = e.dataTransfer;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
    // Reset input value to allow uploading the same file again
    e.target.value = '';
  };

  const handleClickToUpload = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveLogo = () => {
    deleteMutation.mutate();
  };

  const isDeleting = deleteMutation.isLoading;

  return (
    <div className="col-span-6 mt-4 md:col-span-3">
      <label className="block mb-2 text-sm font-medium text-gray-700">Organization Logo</label>

      {isUploading && (
        // Uploading state
        <div className="p-6 text-center border-2 border-dashed rounded-md shadow-sm border-oncolensOrange-500">
          <div className="flex flex-col items-center justify-center">
            <ImSpinner2 className="w-8 h-8 text-oncolensOrange-500 animate-spin" />
            <p className="mt-3 text-sm font-medium text-oncolensOrange-600">Uploading...</p>
          </div>
        </div>
      )}

      {!isUploading && !hasLogo && (
        // Empty state - upload area
        <div
          role="button"
          tabIndex={0}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClickToUpload}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClickToUpload();
            }
          }}
          aria-label="Upload organization logo"
          className={`
            relative border-2 border-dashed border-oncolensOrange-500 rounded-md p-6 text-center cursor-pointer
            transition-colors duration-200 shadow-sm
            ${
              isDragging
                ? 'bg-oncolensOrange-50 border-oncolensOrange-600'
                : 'hover:border-oncolensOrange-600'
            }
          `}>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS.join(',')}
            onChange={handleFileInputChange}
            className="hidden"
          />

          <FaUpload className="w-8 h-8 mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            <span className="font-medium text-oncolensOrange-600">Click to upload</span> or drag and
            drop
          </p>
          <p className="mt-1 text-xs text-gray-500">PNG, JPG, or JPEG (max 5MB)</p>
        </div>
      )}

      {!isUploading && hasLogo && (
        // Logo display state
        <div className="p-4 border rounded-md border-oncolensOrange-500">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <img
                src={getLogoUrl(organizationId, logoTimestamp)}
                alt="Current organization logo thumbnail"
                className="object-contain w-24 h-24 border border-gray-200 rounded"
              />
            </div>

            <div className="flex-1 min-w-0">
              <button
                type="button"
                onClick={() => toggleDeleteModal()}
                disabled={isDeleting}
                aria-label={isDeleting ? 'Removing logo...' : 'Remove organization logo'}
                className={`
                  inline-flex items-center px-3 py-1.5 border border-transparent
                  text-sm font-medium rounded text-white bg-red-600 hover:bg-red-700
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500
                  ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}
                `}>
                <FaTrash className="w-3 h-3 mr-2" />
                Remove Logo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        toggleOverride={toggleDeleteModal}
        testSelector="delete-logo-modal">
        <Modal.Header title="Remove Organization Logo" showClose />
        <Modal.Content>
          <p className="text-sm text-gray-700">
            Are you sure you want to remove this logo? This action cannot be undone.
          </p>
        </Modal.Content>
        <Modal.Buttons className="flex space-x-2.5 items-center justify-end">
          <DefaultButton
            onClick={() => toggleDeleteModal()}
            color={DefaultButtonColor.Neutral}
            disabled={isDeleting}>
            Cancel
          </DefaultButton>
          <DefaultButton
            onClick={handleRemoveLogo}
            color={DefaultButtonColor.Destructive}
            disabled={isDeleting}>
            Yes, Remove
          </DefaultButton>
        </Modal.Buttons>
      </Modal>
    </div>
  );
}
